/**
 * ChatImageProcessingService
 *
 * Canvas-based resize + JPEG-compress for chat images, performed CLIENT-SIDE.
 *
 * WHY this is a new owner file: the chat-image resize/compression USED to run
 * on the server (two ffmpeg scale passes in `transcode_chat_image.ts`). Chat
 * media is now client-side E2EE (story Context), so the server can never see
 * plaintext to resize — the client must produce BOTH the full image and the
 * thumbnail before encrypting + uploading. That is a distinct responsibility
 * (image transcoding) with no existing owner, so per `conventions.md`
 * ("new responsibility → new owner file") it gets its own service rather than
 * being bolted onto `chatService`/`mediaUtils`.
 *
 * Web-first design — there is no Flutter parity for this path; the resize
 * targets approximate the server ffmpeg scale passes (see
 * `chatImageProcessingConstants.ts`).
 *
 * TESTABILITY: the DOM/canvas primitives are injected via the constructor
 * (`decodeImage`, `renderToJpeg`). The defaults use `createImageBitmap` and a
 * real `<canvas>`; tests inject lightweight fakes so the orchestration can be
 * exercised under happy-dom without a real canvas (which has no 2D raster).
 */

import { appLogger } from '@/domain/services/analytics/appLogger';
import { CHAT_IMAGE_PROCESSING_CONSTANTS } from '@/domain/constants/chatImageProcessingConstants';

/** A decoded source image: its intrinsic pixel dimensions + the drawable. */
export interface DecodedImage {
  width: number;
  height: number;
  /** The raster source `drawImage` accepts (ImageBitmap in production). */
  source: CanvasImageSource;
  /** Release decode resources (ImageBitmap.close); no-op for fakes. */
  close: () => void;
}

/** Decode a source image Blob to its dimensions + a drawable raster. */
export type DecodeImageFn = (blob: Blob) => Promise<DecodedImage>;

/**
 * Draw `decoded` scaled to `targetWidth`x`targetHeight` and export as a JPEG
 * Blob at `quality`. Isolated so tests can replace the canvas raster step.
 */
export type RenderToJpegFn = (
  decoded: DecodedImage,
  targetWidth: number,
  targetHeight: number,
  quality: number,
) => Promise<Blob>;

/** The two client-produced renditions of one source chat image. */
export interface ProcessedChatImage {
  /** Full image — longest side ≈ `FULL_IMAGE_MAX_SIDE`, aspect preserved. */
  fullImage: Blob;
  /** Thumbnail — longest side ≈ `THUMBNAIL_MAX_SIDE`, aspect preserved. */
  thumbnail: Blob;
}

/**
 * Compute the scaled-down dimensions for a `maxSide` longest-side box,
 * preserving aspect ratio and NEVER upscaling (mirrors ffmpeg's
 * `force_original_aspect_ratio=decrease`: a smaller source is left as-is).
 */
function fitWithin(
  width: number,
  height: number,
  maxSide: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  // `decrease`: only ever shrink (scale <= 1).
  const scale = Math.min(1, maxSide / longest);
  return {
    // ffmpeg's `-2` keeps each dimension even; rounding to an integer is the
    // canvas equivalent (a fractional canvas size is invalid).
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Default decoder: `createImageBitmap` (handles EXIF-free raster decode). */
const defaultDecodeImage: DecodeImageFn = async (blob: Blob): Promise<DecodedImage> => {
  const bitmap = await createImageBitmap(blob);
  return {
    width: bitmap.width,
    height: bitmap.height,
    source: bitmap,
    close: () => bitmap.close(),
  };
};

/** Default renderer: draw onto a real `<canvas>` and `toBlob('image/jpeg')`. */
const defaultRenderToJpeg: RenderToJpegFn = async (
  decoded: DecodedImage,
  targetWidth: number,
  targetHeight: number,
  quality: number,
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('ChatImageProcessingService: failed to acquire 2D canvas context');
  }
  ctx.drawImage(decoded.source, 0, 0, targetWidth, targetHeight);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, CHAT_IMAGE_PROCESSING_CONSTANTS.OUTPUT_MIME_TYPE, quality);
  });
  if (!blob) {
    throw new Error('ChatImageProcessingService: canvas.toBlob returned null');
  }
  return blob;
};

export class ChatImageProcessingService {
  private readonly decodeImage: DecodeImageFn;
  private readonly renderToJpeg: RenderToJpegFn;

  constructor(deps?: { decodeImage?: DecodeImageFn; renderToJpeg?: RenderToJpegFn }) {
    this.decodeImage = deps?.decodeImage ?? defaultDecodeImage;
    this.renderToJpeg = deps?.renderToJpeg ?? defaultRenderToJpeg;
  }

  /**
   * Resize + JPEG-compress `source` into a full image and a thumbnail.
   *
   * Decodes the source once, then renders it twice (full box, then thumbnail
   * box). Aspect ratio is preserved and the image is never upscaled, matching
   * the server ffmpeg passes this replaces. The decode resource is released in
   * a `finally` so a render failure cannot leak the ImageBitmap.
   *
   * @param source - The user-selected chat image File/Blob.
   * @returns `{ fullImage, thumbnail }` as JPEG Blobs.
   * @throws if decode or either render fails — the caller surfaces the error
   *   (chat send aborts; nothing half-uploaded is encrypted).
   */
  async process(source: Blob): Promise<ProcessedChatImage> {
    let decoded: DecodedImage | null = null;
    try {
      decoded = await this.decodeImage(source);

      const fullDims = fitWithin(
        decoded.width,
        decoded.height,
        CHAT_IMAGE_PROCESSING_CONSTANTS.FULL_IMAGE_MAX_SIDE,
      );
      const thumbDims = fitWithin(
        decoded.width,
        decoded.height,
        CHAT_IMAGE_PROCESSING_CONSTANTS.THUMBNAIL_MAX_SIDE,
      );

      const fullImage = await this.renderToJpeg(
        decoded,
        fullDims.width,
        fullDims.height,
        CHAT_IMAGE_PROCESSING_CONSTANTS.FULL_IMAGE_JPEG_QUALITY,
      );
      const thumbnail = await this.renderToJpeg(
        decoded,
        thumbDims.width,
        thumbDims.height,
        CHAT_IMAGE_PROCESSING_CONSTANTS.THUMBNAIL_JPEG_QUALITY,
      );

      return { fullImage, thumbnail };
    } catch (error) {
      appLogger.error(
        'Failed to process chat image (resize/compress)',
        'ChatImageProcessingService.process',
        { error: error as Error },
      );
      throw error;
    } finally {
      decoded?.close();
    }
  }
}

/** Shared singleton — mirrors the other domain-service singletons. */
export const chatImageProcessingService = new ChatImageProcessingService();
